import { ArrowRight, CheckCircle2, Cpu, Gift, Layers, Palette, Sun, Target } from "lucide-react";
import { DownloadsHeader } from "@/components/ManualLink";
import { FreeUnlockForm, useFreeUnlock } from "@/components/FreeUnlock";
import { DenoiseCompare } from "@/components/DenoiseCompare";
import { BinaryGrid } from "@/components/BinaryGrid";

export const SNOW = "#f2c46d";

const FEATURES = [
  { icon: Sun, title: "Film highlight rolloff", body: "Highlights roll off softly toward white the way film does, instead of the hard digital clip. Shadows and midtones stay untouched." },
  { icon: Target, title: "White point & range", body: "Set where the image reaches full white and how far down the compression reaches, from a gentle shoulder to a deep film curve." },
  { icon: Palette, title: "Negative or slide", body: "Highlight colour goes from negative film (bright areas lose saturation) to slide film (they stay rich)." },
  { icon: Layers, title: "Show rolloff zone", body: "See exactly which part of the image is being compressed, then dial it in." },
  { icon: Cpu, title: "GPU native · ACES ready", body: "Runs in Resolve's GPU memory (Metal, CUDA, OpenCL). Rec.709, ACEScct, ACEScc, ACEScg, ACES2065-1 and DaVinci Wide Gamut." },
  { icon: Gift, title: "Free, no watermark", body: "No licence, no trial, no account. Also built into KolorKlinic Texture, where each film look sets it for you." },
];

const Nudge = () => (
  <a href="#texture" className="group flex items-center justify-between gap-4 border border-[#2a2f3d] bg-[#0e1013] p-4 transition-colors hover:border-[#ff6a3d]" data-testid="film-compression-texture-nudge">
    <span className="text-sm text-[#c9ced8]"><b className="text-white">Want the full film look?</b> KolorKlinic Texture adds halation, diffusion, grain and one-click film looks, with Film Compression built in.</span>
    <span className="flex items-center gap-1 text-sm font-semibold text-[#ff6a3d] whitespace-nowrap">Try Texture <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
  </a>
);

export const FilmCompressionSection = ({ bins, dl, rebuild }) => {
  const [email, unlock] = useFreeUnlock();
  return (
  <section id="film-compression" className="scroll-mt-6 card p-6 space-y-6" data-testid="film-compression-section">
    <div>
      <div className="chip mb-3" style={{ color: SNOW, borderColor: SNOW }} data-testid="film-compression-chip">free · v1.0 · no licence · Metal · CUDA · OpenCL</div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">KolorKlinic <span style={{ color: SNOW }}>Film Compression</span></h2>
      <p className="mt-3 text-sm md:text-base text-[#9ba3b4] max-w-2xl leading-relaxed">
        Film-style highlight compression for DaVinci Resolve. Digital highlights stop clipping and roll off into soft, dense whites,
        so your grade has room to breathe. Free for everyone, our gift to the colour community.
      </p>
    </div>
    <DenoiseCompare id="film-compression" before="filmcomp-before" after="filmcomp-after" color={SNOW} beforeLabel="digital clip" afterLabel="KolorKlinic Film Compression" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {FEATURES.map(({ icon: Icon, title, body }) => (
        <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`film-compression-feature-${title.split(" ")[0].toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: SNOW }} /><h3 className="font-display uppercase text-base">{title}</h3></div>
          <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
    <div>
      <DownloadsHeader title="Free downloads" slug="FilmCompression" name="Film Compression" color={SNOW} testid="filmcomp-manual-download-btn" />
      {email ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-[#a3ff4f]" data-testid="film-compression-unlocked"><CheckCircle2 size={16} /> Unlocked for {email}. The links are in your inbox too.</div>
          <BinaryGrid prefix="rolloff-" bins={bins} dl={dl} rebuild={rebuild} />
        </div>
      ) : <FreeUnlockForm color={SNOW} onUnlock={unlock} />}
    </div>
    <Nudge />
  </section>
  );
};
